/**
 * Encyclopedia — the player-facing unit codex.
 *
 * Browse all units, all star tiers, ability scaling per tier. Reachable
 * at /?encyclopedia.
 *
 * Structure: paper page → centered reading-lane shell → header (eyebrow
 * chip + title + lede + back pill) → sticky FilterBar → row list. Each
 * row is an "atelier card" with a vertical flow: entry strip → name +
 * chips + stats → tier strip → trigger + abilities.
 *
 * Filtering is client-side over the already-fetched units array; no
 * server help needed. Tier/element/animal are single-select facets;
 * name is a free-text contains-match.
 *
 * Tier preview: on desktop a hover portal pops the 420px critter art
 * above the tier cell. On mobile a tap opens a bottom-sheet modal with
 * the same content. <RotatePrompt /> gates portrait phones to landscape
 * per project convention.
 */
import {
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactElement,
} from 'react'
import { createPortal } from 'react-dom'
import type {
  Animal,
  Element,
  ElementsConfig,
  Star,
  SynergyDef,
  UnitDef,
} from '@origami/shared'
import {
  ANIMAL_INFO,
  ELEMENT_INFO,
  HeartStatImg,
  Sparkle,
  StrengthStatImg,
  TieredCritter,
} from '../design-system'
import { RotatePrompt, useIsMobile, useIsFoldOpen } from './useIsMobile'
import { fetchElements, fetchSynergies } from '../game/systemDataApi'
import './Encyclopedia.css'

type FetchState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; units: UnitDef[] }

const TIER_KANJI_NUMERAL: Record<1 | 2 | 3 | 4 | 5, string> = {
  1: '壱',
  2: '弐',
  3: '参',
  4: '肆',
  5: '伍',
}

const TIER_ROMAN: Record<1 | 2 | 3 | 4 | 5, string> = {
  1: 'I',
  2: 'II',
  3: 'III',
  4: 'IV',
  5: 'V',
}

const STARS: Star[] = [1, 2, 3]
const ELEMENT_KEYS: Element[] = ['fire', 'water', 'wind', 'wood', 'earth']
const ANIMAL_KEYS: Animal[] = ['bird', 'mammal', 'reptile', 'fish', 'insect']

type TierFilter = 'all' | 1 | 2 | 3 | 4 | 5
type ElementFilter = 'all' | Element
type AnimalFilter = 'all' | Animal

interface MobilePreview {
  unit: UnitDef
  star: Star
}

type TabId = 'codex' | 'rules'

interface RulesData {
  elements: ElementsConfig
  synergies: SynergyDef[]
}

export function Encyclopedia(): ReactElement {
  const isMobile = useIsMobile()
  const foldOpen = useIsFoldOpen()
  // Fold (Galaxy Fold inner display, 828×690, pointer:coarse) reuses
  // the mobile layout path — single-column flow, smaller tier glyphs,
  // tap-to-modal preview instead of desktop hover-tooltip. RotatePrompt
  // stays gated on `isMobile` only since Fold is already in landscape.
  const mobileLayout = isMobile || foldOpen
  const [state, setState] = useState<FetchState>({ kind: 'loading' })
  const [retryToken, setRetryToken] = useState(0)
  const [tab, setTab] = useState<TabId>('codex')
  const [rules, setRules] = useState<RulesData | null>(null)
  const [tier, setTier] = useState<TierFilter>('all')
  const [element, setElement] = useState<ElementFilter>('all')
  const [animal, setAnimal] = useState<AnimalFilter>('all')
  const [query, setQuery] = useState('')
  const [preview, setPreview] = useState<MobilePreview | null>(null)

  useEffect(() => {
    let cancelled = false
    setState({ kind: 'loading' })
    fetch('/api/units')
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return (await r.json()) as UnitDef[]
      })
      .then((units) => {
        if (cancelled) return
        const sorted = units
          .filter((u) => !u.is_token)
          .sort((a, b) => a.tier - b.tier || a.name.localeCompare(b.name))
        setState({ kind: 'ready', units: sorted })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const message = err instanceof Error ? err.message : String(err)
        setState({ kind: 'error', message })
      })
    return () => {
      cancelled = true
    }
  }, [retryToken])

  // System data for the Rules tab — small + static, fetched once on
  // mount regardless of which tab is active. If the fetch fails the
  // tab simply shows a Loading… and gracefully degrades.
  useEffect(() => {
    let cancelled = false
    Promise.all([fetchElements(), fetchSynergies()])
      .then(([elements, synergies]) => {
        if (!cancelled) setRules({ elements, synergies })
      })
      .catch(() => {
        /* Rules tab handles the null state */
      })
    return () => {
      cancelled = true
    }
  }, [])

  const units = state.kind === 'ready' ? state.units : []

  const availableTiers = useMemo<Array<1 | 2 | 3 | 4 | 5>>(() => {
    const seen = new Set<number>()
    for (const u of units) seen.add(u.tier)
    return [...seen].sort() as Array<1 | 2 | 3 | 4 | 5>
  }, [units])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return units.filter((u) => {
      if (tier !== 'all' && u.tier !== tier) return false
      if (element !== 'all' && u.element !== element) return false
      if (animal !== 'all' && u.animal !== animal) return false
      if (q && !u.name.toLowerCase().includes(q)) return false
      return true
    })
  }, [units, tier, element, animal, query])

  const hasActiveFilter =
    tier !== 'all' ||
    element !== 'all' ||
    animal !== 'all' ||
    query.trim() !== ''

  const clearFilters = () => {
    setTier('all')
    setElement('all')
    setAnimal('all')
    setQuery('')
  }

  const goBack = () => {
    window.history.length > 1 ? window.history.back() : window.location.assign('/')
  }

  return (
    <div className="paper enc-page">
      {isMobile && <RotatePrompt />}
      <div className="enc-shell">
        {mobileLayout && (
          <div className="enc-toprow">
            <button
              type="button"
              className="enc-back hand-note"
              onClick={goBack}
              aria-label="Back"
            >
              ← back
            </button>
            <div className="enc-tabs" role="tablist" aria-label="Encyclopedia sections">
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'codex'}
                className={`enc-tab${tab === 'codex' ? ' enc-tab--active' : ''}`}
                onClick={() => setTab('codex')}
              >
                <span className="enc-tab__kanji">図</span>
                <span>Codex</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'rules'}
                className={`enc-tab${tab === 'rules' ? ' enc-tab--active' : ''}`}
                onClick={() => setTab('rules')}
              >
                <span className="enc-tab__kanji">則</span>
                <span>Rules</span>
              </button>
            </div>
            <div className="enc-stamp enc-stamp--lg enc-stamp--corner">
              <Sparkle size={14} color="var(--vermillion-deep)" />
              <span className="enc-stamp__label">ENCYCLOPEDIA · 百科</span>
            </div>
          </div>
        )}

        <header className="enc-header">
          {!mobileLayout && (
            <>
              <button
                type="button"
                className="enc-back hand-note"
                onClick={goBack}
                aria-label="Back"
              >
                ← back
              </button>
              <div className="enc-stamp enc-stamp--lg">
                <Sparkle size={14} color="var(--vermillion-deep)" />
                <span className="enc-stamp__label">ENCYCLOPEDIA · 百科</span>
              </div>
              <div className="enc-tabs" role="tablist" aria-label="Encyclopedia sections">
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === 'codex'}
                  className={`enc-tab${tab === 'codex' ? ' enc-tab--active' : ''}`}
                  onClick={() => setTab('codex')}
                >
                  <span className="enc-tab__kanji">図</span>
                  <span>Codex</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === 'rules'}
                  className={`enc-tab${tab === 'rules' ? ' enc-tab--active' : ''}`}
                  onClick={() => setTab('rules')}
                >
                  <span className="enc-tab__kanji">則</span>
                  <span>Rules</span>
                </button>
              </div>
            </>
          )}
          <h1 className="cute-title enc-title">
            {tab === 'codex' ? 'Codex of Folded Things' : 'Laws of the Fold'}
          </h1>
          <p className="hand-note enc-lede">
            {tab === 'codex'
              ? 'Every unit, three star tiers, ability scaling per fold. Combine same-star copies to advance.'
              : 'How the elements clash on the frontline, and what allies grant each other when they share a family.'}
          </p>
        </header>

        {tab === 'codex' && state.kind === 'ready' && (
          <FilterBar
            tier={tier}
            setTier={setTier}
            element={element}
            setElement={setElement}
            animal={animal}
            setAnimal={setAnimal}
            query={query}
            setQuery={setQuery}
            availableTiers={availableTiers}
            hasActiveFilter={hasActiveFilter}
            clearFilters={clearFilters}
            shown={filtered.length}
            total={units.length}
          />
        )}

        {tab === 'rules' && <RulesTab data={rules} />}

        {tab === 'codex' && <main className="enc-main">
          {state.kind === 'loading' && (
            <div className="enc-loading hand-note">Loading encyclopedia…</div>
          )}

          {state.kind === 'error' && (
            <div className="enc-error">
              <div className="cute-card enc-error__inner">
                <div className="cute-title" style={{ fontSize: 22 }}>
                  Couldn't load units
                </div>
                <div className="hand-note">{state.message}</div>
                <button
                  className="cute-btn"
                  onClick={() => setRetryToken((t) => t + 1)}
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {state.kind === 'ready' && filtered.length === 0 && (
            <div className="enc-empty">
              <div className="hand-note">No units match the current filters.</div>
              {hasActiveFilter && (
                <button
                  type="button"
                  className="enc-empty__clear hand-note"
                  onClick={clearFilters}
                >
                  ✕ clear filters
                </button>
              )}
            </div>
          )}

          {state.kind === 'ready' && filtered.length > 0 && (
            <ol className="enc-list">
              {filtered.map((unit, i) => (
                <EncyclopediaRow
                  key={unit.id}
                  unit={unit}
                  entry={i + 1}
                  mobileLayout={mobileLayout}
                  onTapTier={(star) => setPreview({ unit, star })}
                />
              ))}
            </ol>
          )}
        </main>}

        <div className="enc-stamp" aria-hidden>
          <Sparkle size={12} color="var(--vermillion-deep)" />
          <span className="enc-stamp__label">折式 · ORIGAMI ARENA</span>
        </div>
      </div>

      {preview && mobileLayout &&
        createPortal(
          <MobileTierSheet
            unit={preview.unit}
            star={preview.star}
            onDismiss={() => setPreview(null)}
          />,
          document.body,
        )}
    </div>
  )
}

/* ============================================================ */
/* Filter bar                                                   */
/* ============================================================ */

interface FilterBarProps {
  tier: TierFilter
  setTier: (v: TierFilter) => void
  element: ElementFilter
  setElement: (v: ElementFilter) => void
  animal: AnimalFilter
  setAnimal: (v: AnimalFilter) => void
  query: string
  setQuery: (v: string) => void
  availableTiers: Array<1 | 2 | 3 | 4 | 5>
  hasActiveFilter: boolean
  clearFilters: () => void
  shown: number
  total: number
}

function FilterBar(props: FilterBarProps): ReactElement {
  const {
    tier, setTier, element, setElement, animal, setAnimal,
    query, setQuery, availableTiers, hasActiveFilter,
    clearFilters, shown, total,
  } = props

  return (
    <aside className="enc-filterbar" aria-label="Filter units">
      <FilterRow label="Tier">
        <FilterPill
          active={tier === 'all'}
          onClick={() => setTier('all')}
          label="All"
        />
        {availableTiers.map((t) => (
          <FilterPill
            key={t}
            active={tier === t}
            onClick={() => setTier(t)}
            label={`${TIER_KANJI_NUMERAL[t]} · ${t}`}
            title={`Tier ${t}`}
          />
        ))}
      </FilterRow>

      <FilterRow label="Element">
        <FilterPill
          active={element === 'all'}
          onClick={() => setElement('all')}
          label="All"
        />
        {ELEMENT_KEYS.map((el) => {
          const info = ELEMENT_INFO[el]
          return (
            <FilterPill
              key={el}
              active={element === el}
              onClick={() => setElement(el)}
              label={`${info.kanji} ${info.en}`}
              style={
                element === el
                  ? { background: info.bg, color: info.fg, borderColor: 'var(--ink)' }
                  : undefined
              }
              title={info.romaji}
            />
          )
        })}
      </FilterRow>

      <FilterRow label="Animal">
        <FilterPill
          active={animal === 'all'}
          onClick={() => setAnimal('all')}
          label="All"
        />
        {ANIMAL_KEYS.map((a) => {
          const info = ANIMAL_INFO[a]
          return (
            <FilterPill
              key={a}
              active={animal === a}
              onClick={() => setAnimal(a)}
              label={`${info.kanji} ${info.en}`}
              style={
                animal === a
                  ? { background: info.bg, color: info.accent, borderColor: 'var(--ink)' }
                  : undefined
              }
            />
          )
        })}
      </FilterRow>

      <div className="enc-filterbar__foot">
        <label className="enc-search">
          <span className="enc-search__icon" aria-hidden>🔍</span>
          <input
            className="enc-search__input"
            type="search"
            placeholder="search by name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search by name"
          />
          {query && (
            <button
              type="button"
              className="enc-search__clear"
              onClick={() => setQuery('')}
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </label>

        <div className="enc-filterbar__count hand-note">
          Showing <strong>{shown}</strong> of {total}
          {hasActiveFilter && (
            <>
              {' · '}
              <button
                type="button"
                className="enc-filterbar__clear"
                onClick={clearFilters}
              >
                ✕ clear filters
              </button>
            </>
          )}
        </div>
      </div>
    </aside>
  )
}

function FilterRow({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}): ReactElement {
  return (
    <div className="enc-filterrow">
      <span className="tiny-cap enc-filterrow__label">{label}</span>
      <div className="enc-filterrow__pills">{children}</div>
    </div>
  )
}

function FilterPill({
  active,
  onClick,
  label,
  style,
  title,
}: {
  active: boolean
  onClick: () => void
  label: string
  style?: CSSProperties
  title?: string
}): ReactElement {
  return (
    <button
      type="button"
      className={`enc-pill${active ? ' enc-pill--active' : ''}`}
      onClick={onClick}
      style={style}
      aria-pressed={active}
      title={title}
    >
      {label}
    </button>
  )
}

/* ============================================================ */
/* Row                                                          */
/* ============================================================ */

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function formatTrigger(trigger: string): string {
  return trigger.replace(/_/g, ' ').toUpperCase()
}

type DesktopHover = { star: Star; rect: DOMRect } | null

function EncyclopediaRow({
  unit,
  entry,
  mobileLayout,
  onTapTier,
}: {
  unit: UnitDef
  entry: number
  // True for both phone landscape AND Fold inner display. Drives
  // the single-column flow + tap-to-modal interaction instead of
  // the desktop's hover-anchored tier preview.
  mobileLayout: boolean
  onTapTier: (star: Star) => void
}): ReactElement {
  const [hover, setHover] = useState<DesktopHover>(null)

  const elementInfo = ELEMENT_INFO[unit.element]
  const animalInfo = ANIMAL_INFO[unit.animal]
  const rowStyle = {
    '--enc-element-color': elementInfo.bg,
    animationDelay: `${entry * 60}ms`,
  } as CSSProperties

  return (
    <li className="enc-row-wrap" style={{ animationDelay: `${entry * 60}ms` }}>
      <article
        className="cute-card enc-row"
        style={rowStyle}
        data-kanji={elementInfo.kanji}
      >
        <div className="enc-row__strip">
          <span className="enc-entry">ENTRY №{String(entry).padStart(2, '0')}</span>
          <span className="enc-row__rule" aria-hidden />
          <span
            className="enc-seal"
            title={`Tier ${unit.tier} · ${TIER_KANJI_NUMERAL[unit.tier]}`}
            aria-label={`Tier ${unit.tier}`}
          >
            {TIER_ROMAN[unit.tier]}
          </span>
        </div>

        <header className="enc-row__head">
          <h2 className="cute-title enc-row__name">{unit.name}</h2>
          {mobileLayout ? (
            <div className="enc-row__meta">
              <div className="enc-row__chips">
                <span
                  className="chip enc-chip"
                  style={{ background: elementInfo.bg, color: elementInfo.fg }}
                  title={elementInfo.romaji}
                >
                  <span className="enc-chip__kanji">{elementInfo.kanji}</span>
                  <span>{capitalize(elementInfo.en)}</span>
                </span>
                <span
                  className="enc-chip enc-chip--animal"
                  style={{ background: animalInfo.bg }}
                  title={animalInfo.en}
                >
                  <span
                    className="enc-chip__kanji"
                    style={{ color: animalInfo.accent }}
                  >
                    {animalInfo.kanji}
                  </span>
                  <span>{animalInfo.en}</span>
                </span>
              </div>
              <span className="enc-stats">
                <HeartStatImg value={unit.stats.hp} size={28} />
                <StrengthStatImg value={unit.stats.atk} size={28} />
              </span>
            </div>
          ) : (
            <div className="enc-row__meta">
              <div className="enc-row__pair">
                <span
                  className="chip enc-chip"
                  style={{ background: elementInfo.bg, color: elementInfo.fg }}
                  title={elementInfo.romaji}
                >
                  <span className="enc-chip__kanji">{elementInfo.kanji}</span>
                  <span>{capitalize(elementInfo.en)}</span>
                </span>
                <HeartStatImg value={unit.stats.hp} size={48} />
              </div>
              <div className="enc-row__pair">
                <span
                  className="enc-chip enc-chip--animal"
                  style={{ background: animalInfo.bg }}
                  title={animalInfo.en}
                >
                  <span
                    className="enc-chip__kanji"
                    style={{ color: animalInfo.accent }}
                  >
                    {animalInfo.kanji}
                  </span>
                  <span>{animalInfo.en}</span>
                </span>
                <StrengthStatImg value={unit.stats.atk} size={48} />
              </div>
            </div>
          )}
        </header>

        <ol
          className="enc-row__tiers"
          onMouseLeave={() => setHover(null)}
        >
          {STARS.map((star) => {
            // Desktop has a dedicated right-column for the tier strip
            // so the glyphs paint comfortably larger; mobile keeps the
            // compact sizing that fits the single-column flow.
            const glyphSize = mobileLayout
              ? 84 + (star - 1) * 12   // 84 · 96 · 108
              : 130 + (star - 1) * 16  // 130 · 146 · 162
            return (
              <li
                key={star}
                className="enc-tier"
                data-star={star}
                onMouseEnter={(e) => {
                  if (!mobileLayout) {
                    setHover({
                      star,
                      rect: e.currentTarget.getBoundingClientRect(),
                    })
                  }
                }}
                onClick={() => {
                  if (mobileLayout) onTapTier(star)
                }}
              >
                <span className="enc-tier__star" data-star={star}>
                  {'✦'.repeat(star)}
                </span>
                <div className="enc-tier__art">
                  <div className="bob">
                    <TieredCritter
                      glyph={unit.glyph}
                      star={star}
                      size={glyphSize}
                      unitId={unit.id}
                    />
                  </div>
                </div>
                {star < 3 && (
                  <span className="enc-tier__connector" aria-hidden>→</span>
                )}
              </li>
            )
          })}
        </ol>

        <div className="enc-row__abilities-col">
          <span className="enc-trigger">△ {formatTrigger(unit.ability.trigger)}</span>
          <ul className="enc-row__abilities">
            {STARS.map((star) => {
              const spec = unit.ability.stars[String(star) as '1' | '2' | '3']
              const isMax = star === 3
              return (
                <li
                  key={star}
                  className={`enc-ability ${isMax ? 'enc-ability--max' : ''}`}
                  data-star={star}
                >
                  <span className="enc-ability__marker">{'✦'.repeat(star)}</span>
                  <span>{spec.description}</span>
                </li>
              )
            })}
          </ul>
        </div>
      </article>

      {hover && !mobileLayout &&
        createPortal(
          <TierPreview unit={unit} star={hover.star} rect={hover.rect} />,
          document.body,
        )}
    </li>
  )
}

/* ============================================================ */
/* Tier preview — desktop hover                                 */
/* ============================================================ */

function TierPreview({
  unit,
  star,
  rect,
}: {
  unit: UnitDef
  star: Star
  rect: DOMRect
}): ReactElement {
  const PREVIEW_BLOCK = 540
  const above = rect.top > PREVIEW_BLOCK
  const HALF_W = 240
  const cx = rect.left + rect.width / 2
  const clampedX = Math.max(
    HALF_W + 12,
    Math.min(window.innerWidth - HALF_W - 12, cx),
  )
  const positionStyle: CSSProperties = above
    ? { top: rect.top - 14, left: clampedX }
    : { top: rect.bottom + 14, left: clampedX }
  return (
    <div
      className={`enc-tier-preview enc-tier-preview--${above ? 'above' : 'below'}`}
      style={positionStyle}
      aria-hidden
    >
      <div className="enc-tier-preview__inner" data-star={star}>
        <div className="enc-tier-preview__art">
          <TieredCritter glyph={unit.glyph} star={star} size={420} unitId={unit.id} />
        </div>
        <div className="tiny-cap enc-tier-preview__caption">
          {unit.name} · {'★'.repeat(star)}
        </div>
      </div>
    </div>
  )
}

/* ============================================================ */
/* Tier preview — mobile bottom sheet                           */
/* ============================================================ */

function MobileTierSheet({
  unit,
  star,
  onDismiss,
}: {
  unit: UnitDef
  star: Star
  onDismiss: () => void
}): ReactElement {
  return (
    <div className="enc-sheet" role="dialog" aria-modal="true">
      <div
        className="enc-sheet__backdrop"
        onClick={onDismiss}
        aria-hidden
      />
      <div className="enc-sheet__inner" data-star={star}>
        <button
          type="button"
          className="enc-sheet__close"
          onClick={onDismiss}
          aria-label="Close preview"
        >
          ✕
        </button>
        <div className="enc-sheet__art">
          <TieredCritter glyph={unit.glyph} star={star} size={260} unitId={unit.id} />
        </div>
        <div className="tiny-cap enc-sheet__caption">
          {unit.name} · {'★'.repeat(star)}
        </div>
      </div>
    </div>
  )
}

/* ============================================================ */
/* Rules tab — element cycle + synergy bonuses                  */
/* ============================================================ */

function RulesTab({ data }: { data: RulesData | null }): ReactElement {
  if (!data) {
    return (
      <main className="enc-main">
        <div className="enc-loading hand-note">Loading rules…</div>
      </main>
    )
  }
  const elementSyns = data.synergies.filter((s) => s.axis === 'element')
  const animalSyns = data.synergies.filter((s) => s.axis === 'animal')
  return (
    <main className="enc-main rules-main">
      <section className="rules-section">
        <header className="rules-section__head">
          <h2 className="cute-title">The Five Elements</h2>
          <p className="hand-note">
            Frontline strikes resolve at{' '}
            <strong>1.25×</strong> when an element holds a strength against
            its target, or <strong>0.75×</strong> the other way. Ability
            damage is element-neutral.
          </p>
        </header>
        <ElementPentagram matrix={data.elements.matchup_matrix} />
      </section>

      <section className="rules-section">
        <header className="rules-section__head">
          <h2 className="cute-title">Element Synergies</h2>
          <p className="hand-note">
            Activate when allies share an element. Stat bonuses stack with
            individual abilities.
          </p>
        </header>
        <div className="rules-grid">
          {elementSyns.map((s) => (
            <SynergyCard key={s.key} synergy={s} />
          ))}
        </div>
      </section>

      <section className="rules-section">
        <header className="rules-section__head">
          <h2 className="cute-title">Animal Synergies</h2>
          <p className="hand-note">
            Activate when allies share an animal family. Same threshold rules
            as the element synergies.
          </p>
        </header>
        <div className="rules-grid">
          {animalSyns.map((s) => (
            <SynergyCard key={s.key} synergy={s} />
          ))}
        </div>
      </section>
    </main>
  )
}

function SynergyCard({ synergy }: { synergy: SynergyDef }): ReactElement {
  const isElement = synergy.axis === 'element'
  const info = isElement
    ? ELEMENT_INFO[synergy.key as Element]
    : ANIMAL_INFO[synergy.key as Animal]
  // The two info maps overlap on { kanji, en, bg } but the foreground
  // key differs (element.fg vs animal.accent). Pick the right one.
  const fg = isElement
    ? (info as typeof ELEMENT_INFO[Element]).fg
    : (info as typeof ANIMAL_INFO[Animal]).accent
  return (
    <article className="cute-card synergy-card">
      <header className="synergy-card__head">
        <span
          className="synergy-card__chip"
          style={{ background: info.bg, color: fg }}
        >
          <span className="synergy-card__kanji">{info.kanji}</span>
        </span>
        <div className="synergy-card__title">
          <h3 className="cute-title">{synergy.display_name}</h3>
          <span className="tiny-cap synergy-card__axis">
            {isElement ? 'Element' : 'Animal'}
          </span>
        </div>
      </header>
      <ol className="synergy-card__tiers">
        {synergy.tiers.map((tier) => (
          <li key={tier.threshold} className="synergy-tier">
            <span className="synergy-tier__threshold">×{tier.threshold}</span>
            <div className="synergy-tier__body">
              {tier.stat_bonus && (
                <div className="synergy-tier__bonus">
                  {tier.stat_bonus.hp ? (
                    <span className="synergy-tier__bonus-pill synergy-tier__bonus-pill--hp">
                      +{tier.stat_bonus.hp} HP
                    </span>
                  ) : null}
                  {tier.stat_bonus.atk ? (
                    <span className="synergy-tier__bonus-pill synergy-tier__bonus-pill--atk">
                      +{tier.stat_bonus.atk} ATK
                    </span>
                  ) : null}
                </div>
              )}
              {tier.effect_description && (
                <p className="synergy-tier__effect hand-note">
                  {tier.effect_description}
                </p>
              )}
            </div>
          </li>
        ))}
      </ol>
    </article>
  )
}

/* ============================================================ */
/* Element pentagram — 5 nodes, 10 directed strong-vs edges     */
/* ============================================================ */

/* Arrangement is the "primary beats cycle" of this game's matrix:
 *
 *   wood → earth → fire → wind → water → wood
 *
 * Each step is a real strong-against. Laid around the pentagon as
 * outer edges, this guarantees every element has exactly ONE enemy
 * on each adjacent side and exactly ONE enemy on each diagonal —
 * the most balanced arrangement possible (each pentagon edge
 * contributes 1 to the global "enemy adjacent" count, total 5;
 * spreading them 1-per-element keeps neighbors fair).
 *
 * The pentagram star (diagonals) then naturally encodes the
 * secondary beats cycle: wood → wind → earth → water → fire → wood. */
const PENT_NODES: Array<{ el: Element; angle: number }> = [
  { el: 'wood',  angle: -90 },
  { el: 'earth', angle: -18 },
  { el: 'fire',  angle:  54 },
  { el: 'wind',  angle: 126 },
  { el: 'water', angle: 198 },
]

function ElementPentagram({
  matrix,
}: {
  matrix: ElementsConfig['matchup_matrix']
}): ReactElement {
  const [hover, setHover] = useState<Element | null>(null)
  const W = 500
  const H = 500
  const CX = W / 2
  const CY = H / 2 + 8
  const R = 168
  const NODE_R = 42

  const positions = useMemo(() => {
    const out = {} as Record<Element, { x: number; y: number }>
    for (const n of PENT_NODES) {
      const rad = (n.angle * Math.PI) / 180
      out[n.el] = {
        x: CX + R * Math.cos(rad),
        y: CY + R * Math.sin(rad),
      }
    }
    return out
  }, [CX, CY, R])

  const edges = useMemo<Array<{ from: Element; to: Element }>>(() => {
    const acc: Array<{ from: Element; to: Element }> = []
    for (const a of PENT_NODES) {
      for (const b of PENT_NODES) {
        if (a.el === b.el) continue
        if (matrix[a.el][b.el] > 1) acc.push({ from: a.el, to: b.el })
      }
    }
    return acc
  }, [matrix])

  return (
    <div className="pentagram-wrap">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="pentagram"
        aria-label="Element matchup pentagram"
      >
        <defs>
          {PENT_NODES.map((n) => (
            <marker
              id={`pent-arrow-${n.el}`}
              key={n.el}
              markerWidth="11"
              markerHeight="11"
              refX="9"
              refY="3.5"
              orient="auto"
              markerUnits="strokeWidth"
            >
              <path d="M0,0 L0,7 L9,3.5 z" fill={ELEMENT_INFO[n.el].bg} />
            </marker>
          ))}
        </defs>

        {edges.map((e) => {
          const from = positions[e.from]
          const to = positions[e.to]
          const dx = to.x - from.x
          const dy = to.y - from.y
          const dist = Math.sqrt(dx * dx + dy * dy)
          const ux = dx / dist
          const uy = dy / dist
          const fromX = from.x + ux * NODE_R
          const fromY = from.y + uy * NODE_R
          const toX = to.x - ux * (NODE_R + 6)
          const toY = to.y - uy * (NODE_R + 6)
          const isFrom = hover === e.from
          const isTo = hover === e.to
          const active = isFrom || isTo
          const dimmed = hover !== null && !active
          return (
            <line
              key={`${e.from}-${e.to}`}
              x1={fromX}
              y1={fromY}
              x2={toX}
              y2={toY}
              stroke={ELEMENT_INFO[e.from].bg}
              strokeWidth={active ? 3.5 : 2}
              opacity={dimmed ? 0.12 : isFrom ? 0.95 : isTo ? 0.55 : 0.65}
              markerEnd={`url(#pent-arrow-${e.from})`}
              strokeLinecap="round"
            />
          )
        })}

        {PENT_NODES.map((n) => {
          const p = positions[n.el]
          const info = ELEMENT_INFO[n.el]
          const isHover = hover === n.el
          return (
            <g
              key={n.el}
              onMouseEnter={() => setHover(n.el)}
              onMouseLeave={() => setHover(null)}
              onClick={() => setHover(hover === n.el ? null : n.el)}
              style={{ cursor: 'pointer' }}
            >
              <circle
                cx={p.x}
                cy={p.y}
                r={NODE_R}
                fill={info.bg}
                stroke="var(--ink)"
                strokeWidth={isHover ? 3.5 : 2.5}
                filter={
                  isHover
                    ? 'drop-shadow(0 4px 0 rgba(58, 42, 24, 0.4))'
                    : 'none'
                }
              />
              <text
                x={p.x}
                y={p.y + 11}
                fontSize="32"
                textAnchor="middle"
                fill={info.fg}
                fontFamily="var(--serif)"
                pointerEvents="none"
              >
                {info.kanji}
              </text>
              <text
                x={p.x}
                y={p.y + NODE_R + 22}
                fontSize="14"
                textAnchor="middle"
                fill="var(--ink)"
                fontFamily="var(--sans)"
                fontWeight="700"
                letterSpacing="0.08em"
                pointerEvents="none"
              >
                {info.en.toUpperCase()}
              </text>
            </g>
          )
        })}
      </svg>
      <div className="pentagram-legend hand-note">
        {hover
          ? renderHoverLegend(hover, matrix)
          : 'Hover or tap an element to highlight what it beats and what beats it.'}
      </div>
    </div>
  )
}

function renderHoverLegend(
  el: Element,
  matrix: ElementsConfig['matchup_matrix'],
): ReactElement {
  const beats: Element[] = []
  const losesTo: Element[] = []
  for (const n of PENT_NODES) {
    if (n.el === el) continue
    if (matrix[el][n.el] > 1) beats.push(n.el)
    if (matrix[n.el][el] > 1) losesTo.push(n.el)
  }
  const info = ELEMENT_INFO[el]
  return (
    <>
      <strong>
        {info.kanji} {capitalize(info.en)}
      </strong>{' '}
      beats{' '}
      {beats.map((b, i) => (
        <span key={b} className="pentagram-legend__el">
          {ELEMENT_INFO[b].kanji} {capitalize(ELEMENT_INFO[b].en)}
          {i < beats.length - 1 ? ' · ' : ''}
        </span>
      ))}{' '}
      · loses to{' '}
      {losesTo.map((b, i) => (
        <span key={b} className="pentagram-legend__el">
          {ELEMENT_INFO[b].kanji} {capitalize(ELEMENT_INFO[b].en)}
          {i < losesTo.length - 1 ? ' · ' : ''}
        </span>
      ))}
    </>
  )
}
